import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_COLORS = {
  free: '#94a3b8', basic: '#3b82f6', standard: '#22c55e',
  professional: '#eab308', premium: '#a855f7', elite: '#06b6d4', admin: '#ef4444'
};
const PLAN_NAMES = {
  free: 'Gratuito', basic: 'Basico', standard: 'Estandar',
  professional: 'Profesional', premium: 'Premium', elite: 'Elite', admin: 'Admin'
};

const AdminStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, analyticsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/referrals/admin/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics?period=${period}`, { headers })
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-CL', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    const s = analytics?.summary || {};
    
    doc.setFontSize(20);
    doc.setTextColor(30, 58, 95);
    doc.text('GPT-JDP — Reporte de Analytics', 14, 22);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-CL')} | Periodo: ${period}`, 14, 30);
    doc.line(14, 33, 196, 33);

    doc.setFontSize(14);
    doc.setTextColor(30, 58, 95);
    doc.text('Resumen General', 14, 42);

    doc.autoTable({
      startY: 46,
      head: [['Metrica', 'Valor']],
      body: [
        ['Total Usuarios', String(s.total_users || 0)],
        ['Usuarios Pagos', String(s.total_paid || 0)],
        ['Usuarios Gratuitos', String(s.total_free || 0)],
        ['Administradores', String(s.total_admins || 0)],
        ['Ingresos Totales', `$${(s.total_revenue || 0).toFixed(2)}`],
        ['Total Pagos', String(s.total_payments || 0)],
        ['Generaciones IA', String(s.total_generations || 0)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [30, 58, 95] },
    });

    if (analytics?.plan_distribution?.length) {
      doc.setFontSize(14);
      doc.text('Distribucion por Plan', 14, doc.lastAutoTable.finalY + 14);
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 18,
        head: [['Plan', 'Usuarios']],
        body: analytics.plan_distribution.map(p => [PLAN_NAMES[p.plan] || p.plan, String(p.count)]),
        theme: 'grid',
        headStyles: { fillColor: [30, 58, 95] },
      });
    }

    if (stats?.top_referrers?.length) {
      doc.setFontSize(14);
      doc.text('Top Referidores', 14, doc.lastAutoTable.finalY + 14);
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 18,
        head: [['#', 'Nombre', 'Codigo', 'Referidos', 'Creditos']],
        body: stats.top_referrers.map((r, i) => [
          String(i + 1), r.name || '', r.code || '', String(r.successful_referrals || 0), String(r.total_credits_earned || 0)
        ]),
        theme: 'grid',
        headStyles: { fillColor: [30, 58, 95] },
      });
    }

    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('GPT-JDP | Nexus Nova Systems Spa | Confidencial', 14, 285);
    doc.save(`GPT-JDP_Reporte_${period}_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  if (loading) {
    return (
      <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Header />
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Header />
          <div className="py-12 text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-2">Acceso Denegado</h2>
            <p className="text-gray-600 dark:text-gray-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const summary = analytics?.summary || {};

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Header />
        
        <div className="py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
            <div>
              <h1 data-testid="admin-stats-title" className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white flex items-center gap-3">
                <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
                Panel de Analytics
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Business Intelligence - GPT-JDP</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Period Selector */}
              <div className="flex bg-gray-100 dark:bg-[#283039] rounded-lg p-1">
                {[{v:'7d',l:'7 dias'},{v:'30d',l:'30 dias'},{v:'90d',l:'90 dias'}].map(p => (
                  <button key={p.v} onClick={() => setPeriod(p.v)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${period === p.v ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
                    data-testid={`period-${p.v}`}
                  >{p.l}</button>
                ))}
              </div>
              <button onClick={exportPDF} data-testid="export-pdf-btn"
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center gap-2 text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                PDF
              </button>
              <button onClick={loadData}
                className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors flex items-center gap-2 text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                Actualizar
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <SummaryCard title="Total Usuarios" value={summary.total_users} color="blue" sub="Registrados" />
            <SummaryCard title="Suscriptores" value={summary.total_paid} color="green" sub="Planes pagos" />
            <SummaryCard title="Ingresos" value={`$${(summary.total_revenue || 0).toFixed(0)}`} color="emerald" sub="Total recaudado" />
            <SummaryCard title="Generaciones IA" value={summary.total_generations} color="purple" sub="Usos totales" />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-[#283039] rounded-lg p-1 overflow-x-auto">
            {[
              {id:'overview',label:'Vista General'},
              {id:'revenue',label:'Ingresos'},
              {id:'referrals',label:'Referidos'},
              {id:'users',label:'Usuarios'}
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-white dark:bg-[#1a2530] text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}
              >{tab.label}</button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Registration Chart */}
              <ChartCard title="Registros de Usuarios" subtitle={`Ultimos ${period.replace('d',' dias')}`}>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics?.registrations_by_day || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="label" tick={{fontSize:11}} stroke="#9ca3af" />
                    <YAxis tick={{fontSize:11}} stroke="#9ca3af" allowDecimals={false} />
                    <Tooltip contentStyle={{background:'#1a2530',border:'none',borderRadius:8,color:'#fff'}} />
                    <Area type="monotone" dataKey="registrations" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} strokeWidth={2} name="Registros" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Plan Distribution */}
              <div className="grid md:grid-cols-2 gap-6">
                <ChartCard title="Distribucion por Plan">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={(analytics?.plan_distribution || []).map(p => ({...p, name: PLAN_NAMES[p.plan] || p.plan}))}
                        cx="50%" cy="50%" outerRadius={100} dataKey="count" nameKey="name" label={({name,count}) => `${name}: ${count}`}>
                        {(analytics?.plan_distribution || []).map((p, i) => (
                          <Cell key={i} fill={PLAN_COLORS[p.plan] || '#6b7280'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Usuarios por Plan">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={(analytics?.plan_distribution || []).map(p => ({...p, name: PLAN_NAMES[p.plan] || p.plan}))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                      <XAxis dataKey="name" tick={{fontSize:11}} stroke="#9ca3af" />
                      <YAxis tick={{fontSize:11}} stroke="#9ca3af" allowDecimals={false} />
                      <Tooltip contentStyle={{background:'#1a2530',border:'none',borderRadius:8,color:'#fff'}} />
                      <Bar dataKey="count" name="Usuarios" radius={[6,6,0,0]}>
                        {(analytics?.plan_distribution || []).map((p, i) => (
                          <Cell key={i} fill={PLAN_COLORS[p.plan] || '#6b7280'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </div>
          )}

          {activeTab === 'revenue' && (
            <div className="space-y-6">
              <ChartCard title="Ingresos en el Tiempo" subtitle={`Ultimos ${period.replace('d',' dias')}`}>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics?.revenue_by_day || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="label" tick={{fontSize:11}} stroke="#9ca3af" />
                    <YAxis tick={{fontSize:11}} stroke="#9ca3af" />
                    <Tooltip contentStyle={{background:'#1a2530',border:'none',borderRadius:8,color:'#fff'}} formatter={(v) => [`$${v}`, 'Ingresos']} />
                    <Area type="monotone" dataKey="revenue" stroke="#22c55e" fill="#22c55e" fillOpacity={0.15} strokeWidth={2} name="Ingresos ($)" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Transacciones Diarias">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics?.revenue_by_day || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="label" tick={{fontSize:11}} stroke="#9ca3af" />
                    <YAxis tick={{fontSize:11}} stroke="#9ca3af" allowDecimals={false} />
                    <Tooltip contentStyle={{background:'#1a2530',border:'none',borderRadius:8,color:'#fff'}} />
                    <Bar dataKey="transactions" fill="#8b5cf6" radius={[4,4,0,0]} name="Transacciones" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          )}

          {activeTab === 'referrals' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatBox label="Codigos Generados" value={stats?.referrals?.total_codes_generated || 0} color="text-blue-500" />
                <StatBox label="Referidores Activos" value={stats?.referrals?.active_referrers || 0} color="text-green-500" />
                <StatBox label="Referidos Exitosos" value={stats?.referrals?.successful_referrals || 0} color="text-purple-500" />
                <StatBox label="Pendientes" value={stats?.referrals?.pending_referrals || 0} color="text-yellow-500" />
              </div>
              {/* Top Referrers Table */}
              <div className="bg-white dark:bg-[#1a2530] rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Top Referidores</h3>
                {stats?.top_referrers?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          <th className="pb-3">#</th><th className="pb-3">Usuario</th><th className="pb-3">Codigo</th>
                          <th className="pb-3">Referidos</th><th className="pb-3 text-right">Creditos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.top_referrers.map((r, i) => (
                          <tr key={r.code} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-3 text-lg">{i===0?'1':i===1?'2':i===2?'3':i+1}</td>
                            <td className="py-3"><p className="font-medium text-gray-900 dark:text-white">{r.name}</p><p className="text-xs text-gray-500">{r.email}</p></td>
                            <td className="py-3 font-mono text-sm text-primary">{r.code}</td>
                            <td className="py-3 font-bold">{r.successful_referrals}</td>
                            <td className="py-3 text-right font-semibold text-green-600">+{r.total_credits_earned?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-center text-gray-500 py-8">Aun no hay referidores activos</p>}
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatBox label="Total" value={stats?.users?.total || 0} color="text-blue-500" />
                <StatBox label="Hoy" value={stats?.users?.new_today || 0} color="text-green-500" />
                <StatBox label="Esta Semana" value={stats?.users?.new_this_week || 0} color="text-purple-500" />
                <StatBox label="Este Mes" value={stats?.users?.new_this_month || 0} color="text-orange-500" />
              </div>
              {/* Recent Users */}
              <div className="bg-white dark:bg-[#1a2530] rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Registros Recientes</h3>
                {stats?.recent_users?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          <th className="pb-3">Usuario</th><th className="pb-3">Email</th><th className="pb-3">Plan</th><th className="pb-3 text-right">Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recent_users.map(u => (
                          <tr key={u.id} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-3 font-medium text-gray-900 dark:text-white">{u.name}</td>
                            <td className="py-3 text-sm text-gray-600 dark:text-gray-400">{u.email}</td>
                            <td className="py-3"><span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">{u.plan}</span></td>
                            <td className="py-3 text-right text-sm text-gray-500">{formatDate(u.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-center text-gray-500 py-8">Sin registros recientes</p>}
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};

// Helper components
const SummaryCard = ({title, value, color, sub}) => {
  const colorClasses = {
    blue: 'bg-gradient-to-br from-blue-500 to-blue-600',
    green: 'bg-gradient-to-br from-green-500 to-emerald-600',
    emerald: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    purple: 'bg-gradient-to-br from-purple-500 to-violet-600',
  };
  return (
    <div className={`${colorClasses[color] || colorClasses.blue} rounded-2xl p-5 text-white`} data-testid={`summary-${title.toLowerCase().replace(/\s/g,'-')}`}>
      <p className="text-sm opacity-80">{title}</p>
      <p className="text-3xl font-black">{value || 0}</p>
      <p className="text-xs mt-2 opacity-70">{sub}</p>
    </div>
  );
};

const ChartCard = ({title, subtitle, children}) => (
  <div className="bg-white dark:bg-[#1a2530] rounded-2xl p-6 shadow-lg">
    <div className="mb-4">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
      {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const StatBox = ({label, value, color}) => (
  <div className="bg-white dark:bg-[#1a2530] rounded-2xl p-4 shadow-lg text-center">
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</p>
  </div>
);

export default AdminStats;
